import React, { useState, useEffect } from 'react'
import { X, Bot, Loader2, AlertCircle, CheckCircle, RefreshCw, Shield, Lock } from 'lucide-react'
import { aiService, AIRequest, AIResponse } from '../../services/ai/AIService'
import SimpleMarkdownRenderer from '../common/SimpleMarkdownRenderer'
import { useAuth } from '../../contexts/AuthContext'
import aiUsageService from '../../services/ai/AIUsageService'
import { AIUsageStatus } from '../../types/ai'
import { isProUser } from '../../utils/proUtils'
import ProUpgradeModal from '../auth/ProUpgradeModal'

interface AIModalProps {
  isOpen: boolean
  onClose: () => void
  content: string
  contentType: 'theory' | 'quiz' | 'formula' | 'exercise'
  context?: string
  title?: string
  questionId?: string
  lessonId?: string
  useDatabase?: boolean
}

const AIModal: React.FC<AIModalProps> = ({
  isOpen,
  onClose,
  content,
  contentType,
  context,
  title = 'Hỏi AI',
  questionId,
  lessonId,
  useDatabase = false
}) => {
  const [response, setResponse] = useState<AIResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [usageStatus, setUsageStatus] = useState<AIUsageStatus | null>(null)
  const [usageError, setUsageError] = useState<string | null>(null)
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false)
  const { user } = useAuth()

  const isPro = isProUser(user)

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setResponse(null)
      setError(null)
      setIsLoading(false)
      if (user) {
        refreshUsageStatus()
      } else {
        setUsageStatus(null)
      }
    }
  }, [isOpen, user?.id])

  const refreshUsageStatus = async (force = false) => {
    if (!user) return
    try {
      const status = await aiUsageService.getStatus(user.id, force)
      setUsageStatus(status)
      setUsageError(null)
    } catch (err) {
      setUsageError(err instanceof Error ? err.message : 'Không thể tải trạng thái lượt AI.')
    }
  }

  const handleAskAI = async () => {
    if (!user) {
      setError('Bạn cần đăng nhập để sử dụng tính năng Hỏi AI.')
      setResponse(null)
      return
    }

    // Check usage limit first (applies to both PRO and non-PRO)
    if (usageStatus && usageStatus.remaining <= 0) {
      setError('Bạn đã sử dụng hết lượt hỏi AI cho hôm nay.')
      return
    }

    // Check PRO status second
    if (!isPro) {
      setIsUpgradeModalOpen(true)
      return
    }

    setIsLoading(true)
    setError(null)
    setResponse(null)

    try {
      const request: AIRequest = {
        content,
        contentType,
        context,
        questionId,
        lessonId,
        useDatabase,
        usage: {
          userId: user.id,
          feature: aiUsageService.mapContentTypeToFeature(contentType),
          metadata: {
            lessonId,
            questionId,
            context
          }
        }
      }

      const result = await aiService.explainContent(request)
      setResponse(result)
      
      if (!result.success) {
        setError(result.error || 'Có lỗi xảy ra')
      }

      await refreshUsageStatus(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Có lỗi xảy ra')
    } finally {
      setIsLoading(false)
    }
  }

  const handleRetry = () => {
    handleAskAI()
  }

  const getContentTypeIcon = () => {
    switch (contentType) {
      case 'theory': return '📚'
      case 'quiz': return '❓'
      case 'formula': return '🧮'
      case 'exercise': return '✏️'
      default: return '🤖'
    }
  }

  const getContentTypeLabel = () => {
    switch (contentType) {
      case 'theory': return 'Lý thuyết'
      case 'quiz': return 'Câu hỏi trắc nghiệm'
      case 'formula': return 'Công thức'
      case 'exercise': return 'Bài tập'
      default: return 'Nội dung'
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <Bot className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">{title}</h2>
                <p className="text-blue-100 text-sm">
                  {getContentTypeIcon()} {getContentTypeLabel()}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {user ? (
            usageStatus && (
              <div className="mb-4 flex items-center space-x-2 text-sm text-gray-600">
                <Shield className="w-4 h-4 text-green-600" />
                <span>
                  Lượt AI còn lại hôm nay:{' '}
                  <strong className="text-gray-900">
                    {usageStatus.remaining}/{usageStatus.dailyLimit}
                  </strong>
                </span>
              </div>
            )
          ) : (
            <div className="mb-4 p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-sm text-yellow-900">
              Bạn cần đăng nhập để sử dụng AI và đồng bộ lượt hỏi giữa các thiết bị.
            </div>
          )}

          {usageError && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {usageError}
            </div>
          )}

          {/* Content Preview */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
            <h3 className="font-semibold text-gray-700 mb-2">Nội dung cần giải thích:</h3>
            <div className="text-sm text-gray-600 max-h-32 overflow-y-auto">
              {content.length > 200 ? `${content.substring(0, 200)}...` : content}
            </div>
          </div>

          {/* AI Response Area */}
          <div className="space-y-4">
            {!response && !isLoading && !error && (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Bot className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  Sẵn sàng giải thích nội dung
                </h3>
                <p className="text-gray-500 mb-6">
                  AI sẽ phân tích và giải thích chi tiết nội dung {getContentTypeLabel().toLowerCase()} này
                </p>
                <button
                  onClick={handleAskAI}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <Bot className="w-5 h-5 inline mr-2" />
                  Hỏi AI
                </button>
              </div>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">
                  AI đang phân tích...
                </h3>
                <p className="text-gray-500">
                  Vui lòng chờ trong giây lát
                </p>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-red-800 mb-2">
                      Không thể kết nối với AI
                    </h3>
                    <p className="text-red-700 mb-4">{error}</p>
                    <button
                      onClick={handleRetry}
                      className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
                    >
                      <RefreshCw className="w-4 h-4" />
                      <span>Thử lại</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Success Response */}
            {response?.success && response.explanation && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <div className="flex items-start space-x-3 mb-4">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-green-800 mb-2">
                      Giải thích từ AI
                    </h3>
                    {response.tokensUsed && (
                      <p className="text-sm text-green-600 mb-3">
                        Tokens sử dụng: {response.tokensUsed}
                      </p>
                    )}
                  </div>
                </div>
                
                                 <div className="prose prose-green max-w-none">
                   <div className="bg-white rounded-lg p-4 border border-green-200">
                     <div className="text-gray-800 leading-relaxed">
                       <SimpleMarkdownRenderer content={response.explanation} />
                     </div>
                   </div>
                 </div>

                <div className="mt-4 flex justify-end space-x-3">
                  <button
                    onClick={handleRetry}
                    className="text-green-600 hover:text-green-700 font-medium flex items-center space-x-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span>Hỏi lại</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <ProUpgradeModal 
        isOpen={isUpgradeModalOpen} 
        onClose={() => setIsUpgradeModalOpen(false)} 
      />
    </div>
  )
}

export default AIModal
